import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ContentNetworkLayer from '../../helpers';
import { subjectUsesMath } from '../EquationModal';
import RichEditor from '../RichEditor';
import LaTeXText from '../LaTeXText';

const SUBJECT_COLORS = [
  '#00ADB5',
  '#E91E9C',
  '#8B4563',
  '#2C3E50',
  '#F5A623',
  '#1ABC9C',
  '#B19CD9',
  '#3498DB',
  '#E74C3C',
  '#9B59B6',
];

// Helper to parse steps JSON
const parseSteps = stepsJson => {
  if (!stepsJson) return [];
  try {
    const parsed = JSON.parse(stepsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Helper to strip HTML and clean answer text for display in edit mode
// Converts: <span class="math-equation" ...>$$1:4$$</span> -> $$1:4$$
const cleanAnswerText = (text) => {
  if (!text) return '';
  // Create a temporary element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = text;
  // Get text content (strips all HTML tags)
  return temp.textContent || temp.innerText || '';
};

/* ── Up/Down move buttons ───────────────────────────────────── */
function MoveButtons({ onMove, isFirst, isLast, busy, size = 'md' }) {
  const dim = size === 'sm' ? 24 : 28;
  const fontSize = size === 'sm' ? '0.75rem' : '0.85rem';
  const baseStyle = {
    width: dim,
    height: dim,
    border: '1px solid #e2e8f0',
    background: '#fff',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize,
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    flexShrink: 0,
  };
  const disabledStyle = { opacity: 0.35, cursor: 'not-allowed' };
  return (
    <div style={{ display: 'flex', gap: 4, marginRight: 6 }}>
      <button
        type="button"
        title="Move up"
        disabled={isFirst || busy}
        onClick={e => {
          e.stopPropagation();
          onMove('up');
        }}
        style={{ ...baseStyle, ...((isFirst || busy) ? disabledStyle : null) }}
      >
        ↑
      </button>
      <button
        type="button"
        title="Move down"
        disabled={isLast || busy}
        onClick={e => {
          e.stopPropagation();
          onMove('down');
        }}
        style={{ ...baseStyle, ...((isLast || busy) ? disabledStyle : null) }}
      >
        ↓
      </button>
    </div>
  );
}

/* ── Delete Confirmation Modal ──────────────────────────────── */
function DeleteConfirmModal({ isOpen, onClose, onConfirm, title, message, loading }) {
  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#ef4444' }}>
            Delete {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              width: 28,
              height: 28,
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '0.85rem',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <p style={{ color: '#475569', marginBottom: 24, lineHeight: 1.6, fontSize: '0.9rem' }}>
            {message}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '9px 20px',
                borderRadius: 8,
                fontSize: '0.875rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                background: '#f1f5f9',
                color: '#475569',
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              style={{
                padding: '9px 20px',
                borderRadius: 8,
                fontSize: '0.875rem',
                fontWeight: 600,
                border: '1.5px solid #fca5a5',
                cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#fee2e2' : '#fff',
                color: '#ef4444',
              }}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Inline note editor ─────────────────────────────────────── */
function NoteCard({ note, onSaved, onDeleted, showEquationButton = false }) {
  const [editing, setEditing] = useState(false);
  
  // Support both v2 (snake_case) and v1 (camelCase) field names
  const noteId = note.noteId ?? note['note_id'];
  const noteText = note.noteContent ?? note['note_content'] ?? note.noteText ?? '';
  const stepsText = note.stepsContent ?? note['steps_content'] ?? note.steps;
  
  const [draftNotes, setDraftNotes] = useState(noteText);
  const [draftFacts, setDraftFacts] = useState(note.facts ?? '');
  const [draftSteps, setDraftSteps] = useState(() => parseSteps(stepsText));
  const [showSteps, setShowSteps] = useState(() => parseSteps(stepsText).length > 0);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Keep drafts in sync if the parent refreshes the note
  useEffect(() => {
    if (!editing) {
      const currentNoteText = note.noteContent ?? note['note_content'] ?? note.noteText ?? '';
      const currentStepsText = note.stepsContent ?? note['steps_content'] ?? note.steps;
      setDraftNotes(currentNoteText);
      setDraftFacts(note.facts ?? '');
      const parsedSteps = parseSteps(currentStepsText);
      setDraftSteps(parsedSteps);
      setShowSteps(parsedSteps.length > 0);
    }
  }, [note, editing]);

  // Steps handlers
  const addStep = () => {
    setDraftSteps([...draftSteps, { step_number: draftSteps.length + 1, title: '', content: '' }]);
  };

  const updateStep = (index, field, value) => {
    const updated = [...draftSteps];
    updated[index][field] = value;
    setDraftSteps(updated);
  };

  const removeStep = index => {
    const updated = draftSteps.filter((_, i) => i !== index);
    updated.forEach((step, i) => (step.step_number = i + 1));
    setDraftSteps(updated);
  };

  const moveStep = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === draftSteps.length - 1))
      return;
    const updated = [...draftSteps];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    updated.forEach((step, i) => (step.step_number = i + 1));
    setDraftSteps(updated);
  };

  // Check if steps have content
  const stepsHaveContent =
    draftSteps.length > 0 && draftSteps.some(s => s.title?.trim() || s.content?.trim());
  const originalStepsJson = note.steps || '';
  const currentStepsJson = stepsHaveContent
    ? JSON.stringify(draftSteps.filter(s => s.title?.trim() || s.content?.trim()))
    : '';

  const isDirty =
    draftNotes !== noteText ||
    draftFacts !== (note.facts ?? '') ||
    currentStepsJson !== originalStepsJson;

  const handleEdit = () => {
    setDraftNotes(noteText);
    setDraftFacts(note.facts ?? '');
    const parsedSteps = parseSteps(stepsText);
    setDraftSteps(parsedSteps);
    setShowSteps(parsedSteps.length > 0);
    setEditing(true);
  };

  const handleCancel = () => {
    setDraftNotes(noteText);
    setDraftFacts(note.facts ?? '');
    const parsedSteps = parseSteps(stepsText);
    setDraftSteps(parsedSteps);
    setShowSteps(parsedSteps.length > 0);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    
    const stepsToSave = stepsHaveContent
      ? JSON.stringify(draftSteps.filter(s => s.title?.trim() || s.content?.trim()))
      : null;
    
    try {
      await ContentNetworkLayer.updateNote(noteId, {
        Notes: draftNotes,
        Facts: draftFacts || undefined,
        Steps: stepsToSave,
      });
      toast.success('Note saved!');
      // Patch both v2 (noteContent/stepsContent) and v1 (noteText/steps) field shapes
      // so the read effect in the parent doesn't snap the editor back to stale values.
      onSaved({
        ...note,
        noteContent: draftNotes,
        noteText: draftNotes,
        note_content: draftNotes,
        facts: draftFacts,
        steps: stepsToSave,
        stepsContent: stepsToSave,
        steps_content: stepsToSave,
      });
      setEditing(false);
    } catch (error) {
      console.error('Failed to save note:', error);
      console.error('Note ID:', noteId);
      console.error('Request payload:', { Notes: draftNotes, Facts: draftFacts, Steps: stepsToSave });
      toast.error(`Failed to save note: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await ContentNetworkLayer.deleteNote(noteId);
      toast.success('Note deleted!');
      setShowDeleteConfirm(false);
      if (onDeleted) onDeleted(noteId);
    } catch {
      toast.error('Failed to delete note.');
    } finally {
      setDeleting(false);
    }
  };

  // Render existing steps in view mode
  const existingSteps = parseSteps(stepsText);

  return (
    <div className="note-card">
      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Note"
        message={`Are you sure you want to delete Note #${noteId}? This action cannot be undone.`}
        loading={deleting}
      />

      {/* Note header */}
      <div className="note-card-header">
        <span className="note-id-badge">Note #{noteId}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {!editing && (
            <>
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleEdit}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-danger-ghost btn-sm"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Notes field */}
      <div className="note-field">
        <div className="note-field-label">Notes</div>
        {editing ? (
          <RichEditor
            value={draftNotes}
            onChange={setDraftNotes}
            placeholder="Write your notes here..."
            minHeight={140}
            showEquationButton={showEquationButton}
          />
        ) : (
          <div className="note-html-view">
            {noteText ? (
              <LaTeXText>{noteText}</LaTeXText>
            ) : (
              <em style={{ color: '#94a3b8' }}>No content</em>
            )}
          </div>
        )}
      </div>

      {/* Facts field — always show in edit mode; only show in view if non-empty */}
      {(editing || draftFacts || note.facts) && (
        <div className="note-field">
          <div className="note-field-label">Facts / Sub-text</div>
          {editing ? (
            <RichEditor
              value={draftFacts}
              onChange={setDraftFacts}
              placeholder="Key facts or additional context..."
              minHeight={100}
              showEquationButton={showEquationButton}
            />
          ) : (
            <div className="note-html-view note-html-view--facts">
              {note.facts ? (
                <LaTeXText>{note.facts}</LaTeXText>
              ) : (
                <em style={{ color: '#94a3b8' }}>None</em>
              )}
            </div>
          )}
        </div>
      )}

      {/* Steps Section */}
      {editing ? (
        <div className="note-field">
          <div className="steps-header">
            <div className="note-field-label">Step-by-Step Instructions</div>
            {!showSteps && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowSteps(true);
                  if (draftSteps.length === 0) addStep();
                }}
              >
                + Add Steps
              </button>
            )}
          </div>

          {showSteps && (
            <div className="steps-container">
              {draftSteps.map((step, index) => (
                <div key={index} className="step-card">
                  <div className="step-card-header">
                    <span className="step-number-badge">Step {step.step_number}</span>
                    <div className="step-actions">
                      <button
                        type="button"
                        className="step-move-btn"
                        onClick={() => moveStep(index, -1)}
                        disabled={index === 0}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="step-move-btn"
                        onClick={() => moveStep(index, 1)}
                        disabled={index === draftSteps.length - 1}
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="remove-answer-btn"
                        onClick={() => removeStep(index)}
                        title="Remove step"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="step-fields">
                    <input
                      type="text"
                      className="field-input step-title-input"
                      placeholder="Step title (e.g., 'Calculate the derivative')"
                      value={step.title || ''}
                      onChange={e => updateStep(index, 'title', e.target.value)}
                    />
                    <RichEditor
                      value={step.content || ''}
                      onChange={val => updateStep(index, 'content', val)}
                      placeholder="Detailed explanation for this step..."
                      minHeight={80}
                      showEquationButton={showEquationButton}
                    />
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addStep}>
                + Add Another Step
              </button>
            </div>
          )}
        </div>
      ) : (
        existingSteps.length > 0 && (
          <div className="note-field">
            <div className="note-field-label">Steps ({existingSteps.length})</div>
            <div className="steps-view">
              {existingSteps.map((step, index) => (
                <div key={index} className="step-view-card">
                  <div className="step-view-badge">{step.step_number}</div>
                  <div className="step-view-content">
                    <div className="step-view-title">
                      {step.title || `Step ${step.step_number}`}
                    </div>
                    {step.content && (
                      <div className="step-view-text">
                        <LaTeXText>{step.content}</LaTeXText>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Edit action bar */}
      {editing && (
        <div className="note-edit-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Subtopic Question Card (with Edit support) ─────────────────────────────────── */
function SubtopicQuestionCard({ question, onDeleted, onUpdated, showEquationButton = false }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // Support both v2 (snake_case) and v1 (camelCase) field names
  const questionId = question.questionId ?? question['question_id'];
  const questionText = question.questionContent ?? question.questionText ?? '';
  const [draftQuestion, setDraftQuestion] = useState(questionText);
  const [draftAnswers, setDraftAnswers] = useState([]);
  const [draftTrueFalse, setDraftTrueFalse] = useState(true); // For True/False questions

  // Initialize draft answers when entering edit mode
  const handleEdit = () => {
    setDraftQuestion(question.questionContent ?? question.questionText ?? '');
    
    if (question.type === 'True/False') {
      // For True/False, determine current correct answer
      const isTrue = question.multipleChoiceAnswers?.[0]?.isCorrect === 1;
      setDraftTrueFalse(isTrue);
    } else if (question.type === 'Matching' || question.type === 'MatchingPairs') {
      setDraftAnswers(
        (question.matchingAnswers || []).map(a => ({
          answeredId: a.answeredId,
          answer: cleanAnswerText(a.answerContent ?? a.answer ?? ''),
          order: a.order,
        }))
      );
    } else {
      // MCQ, MultiSelect, FIB
      setDraftAnswers(
        (question.multipleChoiceAnswers || []).map(a => ({
          answeredId: a.answeredId,
          answer: cleanAnswerText(a.answerContent ?? a.answer ?? ''),
          isCorrect: a.isCorrect === 1,
        }))
      );
    }
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setDraftQuestion(question.questionContent ?? question.questionText ?? '');
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      let answersPayload;
      
      if (question.type === 'True/False') {
        // Build True/False answer format
        answersPayload = [
          { answer: 'True', is_correct: draftTrueFalse ? 1 : 0 },
          { answer: 'False', is_correct: draftTrueFalse ? 0 : 1 },
        ];
      } else if (question.type === 'Matching' || question.type === 'MatchingPairs') {
        answersPayload = draftAnswers.map(a => ({
          answer: a.answer,
          order: a.order,
        }));
      } else {
        // MCQ, MultiSelect, FIB
        answersPayload = draftAnswers.map(a => ({
          answer: a.answer,
          is_correct: a.isCorrect ? 1 : 0,
        }));
      }

      await ContentNetworkLayer.updateQuestion(questionId, {
        questionText: draftQuestion,
        answers: answersPayload,
      });
      
      toast.success('Question updated!');
      
      // Update local state with new values
      const isMatchingType = question.type === 'Matching' || question.type === 'MatchingPairs';
      const updatedQuestion = {
        ...question,
        questionText: draftQuestion,
        multipleChoiceAnswers: !isMatchingType ? answersPayload.map((a, idx) => ({
          ...a,
          isCorrect: a.is_correct,
          answeredId: draftAnswers[idx]?.answeredId,
        })) : question.multipleChoiceAnswers,
        matchingAnswers: isMatchingType ? answersPayload.map((a, idx) => ({
          ...a,
          answeredId: draftAnswers[idx]?.answeredId,
        })) : question.matchingAnswers,
      };
      
      if (onUpdated) onUpdated(updatedQuestion);
      setEditing(false);
    } catch (err) {
      toast.error('Failed to update question.');
      console.error('Update question error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await ContentNetworkLayer.deleteSubtopicQuestion(questionId);
      toast.success('Question deleted!');
      setShowDeleteConfirm(false);
      if (onDeleted) onDeleted(questionId);
    } catch {
      toast.error('Failed to delete question.');
    } finally {
      setDeleting(false);
    }
  };

  const updateAnswer = (index, field, value) => {
    const updated = [...draftAnswers];
    updated[index] = { ...updated[index], [field]: value };
    setDraftAnswers(updated);
  };

  const toggleCorrect = index => {
    const updated = [...draftAnswers];
    if (question.type === 'MCQ') {
      // Only one correct for MCQ
      updated.forEach((a, i) => (a.isCorrect = i === index));
    } else {
      // Multiple correct for MultiSelect / FIB
      updated[index].isCorrect = !updated[index].isCorrect;
    }
    setDraftAnswers(updated);
  };

  // Format answers for display (or edit)
  const renderAnswers = () => {
    const type = question.type;

    if (type === 'MCQ' || type === 'MultiSelect' || type === 'FIB') {
      const answers = editing ? draftAnswers : (question.multipleChoiceAnswers || []);
      return (
        <div className="question-answers">
          {answers.map((ans, idx) => (
            <div
              key={idx}
              className={`question-answer-item ${(editing ? ans.isCorrect : ans.isCorrect === 1) ? 'correct' : ''}`}
              style={editing ? { display: 'flex', alignItems: 'center', gap: 8 } : undefined}
            >
              {editing ? (
                <>
                  <input
                    type={type === 'MCQ' ? 'radio' : 'checkbox'}
                    checked={ans.isCorrect}
                    onChange={() => toggleCorrect(idx)}
                    style={{ cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={ans.answer}
                    onChange={e => updateAnswer(idx, 'answer', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      fontSize: '0.85rem',
                    }}
                  />
                </>
              ) : (
                <>
                  <span className="answer-marker">{ans.isCorrect === 1 ? '✓' : '○'}</span>
                  <LaTeXText>{ans.answer}</LaTeXText>
                </>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (type === 'True/False') {
      if (editing) {
        return (
          <div className="question-answers" style={{ display: 'flex', gap: 12 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                background: draftTrueFalse ? '#dcfce7' : '#f8fafc',
                borderRadius: 6,
                border: `1px solid ${draftTrueFalse ? '#86efac' : '#e2e8f0'}`,
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                checked={draftTrueFalse}
                onChange={() => setDraftTrueFalse(true)}
              />
              True
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                background: !draftTrueFalse ? '#dcfce7' : '#f8fafc',
                borderRadius: 6,
                border: `1px solid ${!draftTrueFalse ? '#86efac' : '#e2e8f0'}`,
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                checked={!draftTrueFalse}
                onChange={() => setDraftTrueFalse(false)}
              />
              False
            </label>
          </div>
        );
      }
      const correctAnswer = question.multipleChoiceAnswers?.[0]?.isCorrect ? 'True' : 'False';
      return (
        <div className="question-answers">
          <div className="question-answer-item correct">
            <span className="answer-marker">✓</span>
            <span>Correct answer: {correctAnswer}</span>
          </div>
        </div>
      );
    }

    if (type === 'Matching' || type === 'MatchingPairs') {
      const answers = editing ? draftAnswers : (question.matchingAnswers || []);
      
      // For MatchingPairs: show as term → definition pairs
      if (type === 'MatchingPairs' && answers.length > 0) {
        const half = Math.floor(answers.length / 2);
        const terms = answers.slice(0, half);
        const definitions = answers.slice(half);
        
        // Create pairs by matching order values
        const pairs = terms.map((term, termIdx) => {
          const defIdx = definitions.findIndex(d => d.order === term.order);
          const definition = defIdx >= 0 ? definitions[defIdx] : null;
          return { 
            term, 
            definition, 
            termIdx,
            defIdx: defIdx >= 0 ? half + defIdx : -1
          };
        });
        
        return (
          <div className="question-answers" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              gap: 12, 
              paddingBottom: 6,
              borderBottom: '1px solid #e2e8f0',
              marginBottom: 4,
            }}>
              <span style={{ width: 24 }}></span>
              <span style={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Term
              </span>
              <span style={{ width: 20 }}></span>
              <span style={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Definition
              </span>
            </div>
            
            {pairs.map((pair, idx) => (
              <div
                key={pair.term?.answeredId || idx}
                className="question-answer-item"
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}
              >
                {/* Order badge */}
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#00ADB5',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {pair.term?.order}
                </span>
                
                {editing ? (
                  <>
                    <input
                      type="text"
                      value={pair.term?.answer || ''}
                      onChange={e => updateAnswer(pair.termIdx, 'answer', e.target.value)}
                      placeholder="Term"
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 4,
                        fontSize: '0.85rem',
                        fontWeight: 500,
                      }}
                    />
                    <span style={{ color: '#94a3b8', flexShrink: 0 }}>→</span>
                    <input
                      type="text"
                      value={pair.definition?.answer || ''}
                      onChange={e => updateAnswer(pair.defIdx, 'answer', e.target.value)}
                      placeholder="Definition"
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 4,
                        fontSize: '0.85rem',
                      }}
                    />
                  </>
                ) : (
                  <>
                    <LaTeXText style={{ flex: 1, fontSize: '0.85rem', fontWeight: 500 }}>
                      {pair.term?.answer}
                    </LaTeXText>
                    <span style={{ color: '#94a3b8', flexShrink: 0 }}>→</span>
                    <LaTeXText style={{ flex: 1, fontSize: '0.85rem', color: '#64748b' }}>
                      {pair.definition?.answer}
                    </LaTeXText>
                  </>
                )}
              </div>
            ))}
          </div>
        );
      }
      
      // Legacy Matching type (flat list)
      return (
        <div className="question-answers">
          {answers
            .sort((a, b) => a.order - b.order)
            .map((ans, idx) => (
              <div
                key={idx}
                className="question-answer-item"
                style={editing ? { display: 'flex', alignItems: 'center', gap: 8 } : undefined}
              >
                {editing ? (
                  <>
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: '#00ADB5',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {ans.order}
                    </span>
                    <input
                      type="text"
                      value={ans.answer}
                      onChange={e => updateAnswer(idx, 'answer', e.target.value)}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 4,
                        fontSize: '0.85rem',
                      }}
                    />
                  </>
                ) : (
                  <>
                    <span className="answer-marker">{ans.order}.</span>
                    <LaTeXText>{ans.answer}</LaTeXText>
                  </>
                )}
              </div>
            ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="question-card">
      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Question"
        message={`Are you sure you want to delete this question? This action cannot be undone.`}
        loading={deleting}
      />

      {/* Question header */}
      <div className="question-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="question-type-badge">{question.type}</span>
          <span className="note-id-badge">Q#{questionId}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {!editing && (
            <>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={handleEdit}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn btn-danger-ghost btn-sm"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Question text */}
      <div className="question-text">
        {editing ? (
          <RichEditor
            value={draftQuestion}
            onChange={setDraftQuestion}
            placeholder="Question text..."
            minHeight={80}
            showEquationButton={showEquationButton}
          />
        ) : (
          <div>
            {questionText ? (
              <LaTeXText>{questionText}</LaTeXText>
            ) : (
              <em style={{ color: '#94a3b8' }}>No question text</em>
            )}
          </div>
        )}
      </div>

      {/* Answers */}
      {renderAnswers()}

      {/* Edit actions */}
      {editing && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Subtopic row with lazy-loaded notes and questions ────────────────────── */
function SubtopicRow({ subtopic, showEquationButton = false, onDeleted, onMove, isFirst, isLast, moving }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toggle = () => {
    if (!open && !loaded) {
      setLoading(true);
      // Load both notes and questions in parallel
      Promise.all([
        ContentNetworkLayer.getNotesBySubtopic(subtopic.subTopicId),
        ContentNetworkLayer.getSubtopicQuestions(subtopic.subTopicId),
      ])
        .then(([notesRes, questionsRes]) => {
          setNotes(notesRes.notes || []);
          setQuestions(questionsRes.questions || []);
          setLoaded(true);
        })
        .catch(() => toast.error('Could not load content.'))
        .finally(() => setLoading(false));
    }
    setOpen(prev => !prev);
  };

  const handleNoteSaved = updatedNote => {
    setNotes(prev => prev.map(n => {
      const nId = n.noteId ?? n['note_id'];
      const uId = updatedNote.noteId ?? updatedNote['note_id'];
      return nId === uId ? updatedNote : n;
    }));
  };

  const handleNoteDeleted = noteId => {
    setNotes(prev => prev.filter(n => (n.noteId ?? n['note_id']) !== noteId));
  };

  const handleQuestionDeleted = questionId => {
    setQuestions(prev => prev.filter(q => (q.questionId ?? q['question_id']) !== questionId));
  };

  const handleQuestionUpdated = updatedQuestion => {
    setQuestions(prev => prev.map(q => {
      const qId = q.questionId ?? q['question_id'];
      const uId = updatedQuestion.questionId ?? updatedQuestion['question_id'];
      return qId === uId ? updatedQuestion : q;
    }));
  };

  const handleDeleteSubtopic = async () => {
    setDeleting(true);
    try {
      await ContentNetworkLayer.deleteSubTopic(subtopic.subTopicId);
      toast.success('Subtopic deleted!');
      setShowDeleteConfirm(false);
      if (onDeleted) onDeleted(subtopic.subTopicId);
    } catch {
      toast.error('Failed to delete subtopic.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="subtopic-row-wrapper">
      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSubtopic}
        title="Subtopic"
        message={`Are you sure you want to delete "${subtopic.title}"? This will also delete all notes in this subtopic. This action cannot be undone.`}
        loading={deleting}
      />

      {/* Subtopic header button */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {onMove && (
          <MoveButtons
            onMove={dir => onMove(subtopic.subTopicId, dir)}
            isFirst={isFirst}
            isLast={isLast}
            busy={moving}
            size="sm"
          />
        )}
        <button
          type="button"
          className={`subtopic-row${open ? ' open' : ''}`}
          onClick={toggle}
          style={{ flex: 1 }}
        >
          <span className="subtopic-chevron">{open ? '▾' : '▸'}</span>
          <span className="subtopic-icon">📄</span>
          <span className="subtopic-title">{subtopic.title}</span>
          <span className="subtopic-id-badge">ID {subtopic.subTopicId}</span>
          {loaded && (
            <>
              <span className="note-count-badge">
                {notes.length} note{notes.length !== 1 ? 's' : ''}
              </span>
              <span className="question-count-badge">
                {questions.length} question{questions.length !== 1 ? 's' : ''}
              </span>
            </>
          )}
        </button>
        <button
          type="button"
          className="remove-answer-btn"
          onClick={e => {
            e.stopPropagation();
            setShowDeleteConfirm(true);
          }}
          title="Delete subtopic"
          style={{ marginRight: 12 }}
        >
          🗑️
        </button>
      </div>

      {/* Notes and Questions panel */}
      {open && (
        <div className="notes-panel">
          {loading && <div className="notes-loading">Loading content...</div>}
          
          {/* Notes section */}
          {!loading && loaded && (
            <>
              <div className="panel-section-header">Notes ({notes.length})</div>
              {notes.length === 0 && (
                <div className="notes-empty">No notes for this sub-topic yet.</div>
              )}
              {notes.map(note => (
                <NoteCard
                  key={note.noteId ?? note['note_id']}
                  note={note}
                  onSaved={handleNoteSaved}
                  onDeleted={handleNoteDeleted}
                  showEquationButton={showEquationButton}
                />
              ))}
            </>
          )}

          {/* Questions section */}
          {!loading && loaded && (
            <>
              <div className="panel-section-header" style={{ marginTop: notes.length > 0 ? 20 : 0 }}>
                Questions ({questions.length})
              </div>
              {questions.length === 0 && (
                <div className="notes-empty">No questions for this sub-topic yet.</div>
              )}
              {questions.map(question => (
                <SubtopicQuestionCard
                  key={question.questionId ?? question['question_id']}
                  question={question}
                  onDeleted={handleQuestionDeleted}
                  onUpdated={handleQuestionUpdated}
                  showEquationButton={showEquationButton}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main Browse component ──────────────────────────────────── */
export default function Browse() {
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);

  // Delete state
  const [deleteModal, setDeleteModal] = useState({ open: false, type: '', item: null });
  const [deleting, setDeleting] = useState(false);

  // Reorder state — tracks ids currently being moved so buttons can disable.
  const [movingTopicIds, setMovingTopicIds] = useState(() => new Set());
  const [movingSubtopicIds, setMovingSubtopicIds] = useState(() => new Set());

  const load = () => {
    setLoading(true);
    Promise.all([
      ContentNetworkLayer.getSubjects(),
      ContentNetworkLayer.getTopics(),
      ContentNetworkLayer.getSubTopics(),
    ])
      .then(([subRes, topRes, stRes]) => {
        setSubjects(subRes.subjects || []);
        setTopics(topRes.topics || []);
        setSubtopics(stRes.subTopics || []);
        setLastRefresh(new Date());
      })
      .catch(() => toast.error('Could not load content.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filteredTopics = selectedSubject ? topics.filter(t => t.code === selectedSubject.code) : [];

  // Check if selected subject uses math symbols
  const showEquationButton = subjectUsesMath(selectedSubject?.name);

  const toggleTopic = topicId =>
    setExpandedTopics(prev => ({ ...prev, [topicId]: !prev[topicId] }));

  const subtopicsForTopic = topicId => subtopics.filter(st => st.topicId === topicId);

  const subjectColor = i => SUBJECT_COLORS[i % SUBJECT_COLORS.length];

  // Delete handlers
  const handleDeleteSubject = async () => {
    if (!deleteModal.item) return;
    setDeleting(true);
    try {
      await ContentNetworkLayer.deleteSubject(deleteModal.item.code);
      toast.success('Subject deleted!');
      setSubjects(prev => prev.filter(s => s.code !== deleteModal.item.code));
      setTopics(prev => prev.filter(t => t.code !== deleteModal.item.code));
      if (selectedSubject?.code === deleteModal.item.code) {
        setSelectedSubject(null);
      }
      setDeleteModal({ open: false, type: '', item: null });
    } catch {
      toast.error('Failed to delete subject.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!deleteModal.item) return;
    setDeleting(true);
    try {
      await ContentNetworkLayer.deleteTopic(deleteModal.item.topicId);
      toast.success('Topic deleted!');
      setTopics(prev => prev.filter(t => t.topicId !== deleteModal.item.topicId));
      setSubtopics(prev => prev.filter(st => st.topicId !== deleteModal.item.topicId));
      setDeleteModal({ open: false, type: '', item: null });
    } catch {
      toast.error('Failed to delete topic.');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubtopicDeleted = subTopicId => {
    setSubtopics(prev => prev.filter(st => st.subTopicId !== subTopicId));
  };

  // ── Reorder handlers ────────────────────────────────────────
  // Optimistically swap two items in an array (by id field), call the API,
  // revert on failure. The `prev` snapshot is what we revert to.
  const handleMoveTopic = async (topicId, direction) => {
    if (movingTopicIds.has(topicId)) return;
    const subjectTopics = topics.filter(t => t.code === selectedSubject?.code);
    const idx = subjectTopics.findIndex(t => t.topicId === topicId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swapIdx < 0 || swapIdx >= subjectTopics.length) return;
    const otherId = subjectTopics[swapIdx].topicId;

    const prev = topics;
    // Swap the two topics in the master list while preserving relative order
    // of all other topics (including those from other subjects).
    const next = [...topics];
    const a = next.findIndex(t => t.topicId === topicId);
    const b = next.findIndex(t => t.topicId === otherId);
    [next[a], next[b]] = [next[b], next[a]];
    setTopics(next);

    setMovingTopicIds(s => new Set(s).add(topicId));
    try {
      await ContentNetworkLayer.moveTopic(topicId, direction);
    } catch (err) {
      console.error('Move topic failed:', err);
      toast.error('Failed to move topic.');
      setTopics(prev);
    } finally {
      setMovingTopicIds(s => {
        const next2 = new Set(s);
        next2.delete(topicId);
        return next2;
      });
    }
  };

  const handleMoveSubtopic = async (subTopicId, direction) => {
    if (movingSubtopicIds.has(subTopicId)) return;
    const me = subtopics.find(st => st.subTopicId === subTopicId);
    if (!me) return;
    const siblings = subtopics.filter(st => st.topicId === me.topicId);
    const idx = siblings.findIndex(st => st.subTopicId === subTopicId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const otherId = siblings[swapIdx].subTopicId;

    const prev = subtopics;
    const next = [...subtopics];
    const a = next.findIndex(st => st.subTopicId === subTopicId);
    const b = next.findIndex(st => st.subTopicId === otherId);
    [next[a], next[b]] = [next[b], next[a]];
    setSubtopics(next);

    setMovingSubtopicIds(s => new Set(s).add(subTopicId));
    try {
      await ContentNetworkLayer.moveSubtopic(subTopicId, direction);
    } catch (err) {
      console.error('Move subtopic failed:', err);
      toast.error('Failed to move subtopic.');
      setSubtopics(prev);
    } finally {
      setMovingSubtopicIds(s => {
        const next2 = new Set(s);
        next2.delete(subTopicId);
        return next2;
      });
    }
  };

  const handleDelete = () => {
    if (deleteModal.type === 'subject') handleDeleteSubject();
    else if (deleteModal.type === 'topic') handleDeleteTopic();
  };

  const getDeleteMessage = () => {
    if (deleteModal.type === 'subject') {
      return `Are you sure you want to delete "${deleteModal.item?.name}"? This will also delete ALL topics, subtopics, notes, and questions for this subject. This action cannot be undone.`;
    }
    if (deleteModal.type === 'topic') {
      return `Are you sure you want to delete "${deleteModal.item?.name}"? This will also delete all subtopics and notes in this topic. This action cannot be undone.`;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="form-card" style={{ textAlign: 'center', padding: 48 }}>
        <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading content...</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, type: '', item: null })}
        onConfirm={handleDelete}
        title={deleteModal.type === 'subject' ? 'Subject' : 'Topic'}
        message={getDeleteMessage()}
        loading={deleting}
      />
      {/* ── Subject sidebar ─────────────────────────────────── */}
      <div className="form-card" style={{ width: 260, flexShrink: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a202c' }}>Subjects</div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
              {subjects.length} total
            </div>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={load}
            title="Refresh"
            style={{ padding: '5px 10px' }}
          >
            ↻
          </button>
        </div>

        {subjects.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center', padding: 16 }}>
            No subjects yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {subjects.map((s, i) => {
              const topicCount = topics.filter(t => t.code === s.code).length;
              const isSelected = selectedSubject?.code === s.code;
              return (
                <div
                  key={s.code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    border: `1.5px solid ${isSelected ? subjectColor(i) : '#e2e8f0'}`,
                    borderRadius: 8,
                    background: isSelected ? `${subjectColor(i)}14` : '#f8fafc',
                    transition: 'all 0.15s',
                    paddingRight: 8,
                  }}
                >
                  <button
                    onClick={() => {
                      setSelectedSubject(isSelected ? null : s);
                      setExpandedTopics({});
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: subjectColor(i),
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '0.82rem',
                          color: '#1a202c',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {s.name}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                        {s.level} · {topicCount} topic{topicCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteModal({ open: true, type: 'subject', item: s });
                    }}
                    title="Delete subject"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8',
                      fontSize: '0.85rem',
                      padding: '4px 6px',
                      borderRadius: 4,
                      flexShrink: 0,
                    }}
                  >
                    🗑️
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {lastRefresh && (
          <div style={{ marginTop: 12, fontSize: '0.68rem', color: '#94a3b8', textAlign: 'right' }}>
            Updated {lastRefresh.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* ── Topic / subtopic / notes tree ───────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selectedSubject ? (
          <div className="form-card" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>📚</div>
            <div style={{ fontWeight: 600, color: '#1a202c', marginBottom: 4 }}>
              Select a subject
            </div>
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
              Click a subject to browse its topics, sub-topics, and notes.
            </div>
          </div>
        ) : (
          <div className="form-card">
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c' }}>
                {selectedSubject.name}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                {selectedSubject.level}
                {selectedSubject.description && ` · ${selectedSubject.description}`}
              </div>
            </div>

            {filteredTopics.length === 0 ? (
              <div
                style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: 24 }}
              >
                No topics yet for this subject.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filteredTopics.map((topic, topicIdx) => {
                  const subs = subtopicsForTopic(topic.topicId);
                  const isOpen = expandedTopics[topic.topicId];
                  const isFirstTopic = topicIdx === 0;
                  const isLastTopic = topicIdx === filteredTopics.length - 1;
                  return (
                    <div
                      key={topic.topicId}
                      style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}
                    >
                      {/* Topic header */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          background: isOpen ? '#f0fdfa' : '#f8fafc',
                          paddingLeft: 12,
                        }}
                      >
                        <MoveButtons
                          onMove={dir => handleMoveTopic(topic.topicId, dir)}
                          isFirst={isFirstTopic}
                          isLast={isLastTopic}
                          busy={movingTopicIds.has(topic.topicId)}
                        />
                        <button
                          type="button"
                          onClick={() => toggleTopic(topic.topicId)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            flex: 1,
                            padding: '12px 16px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '0.8rem',
                              color: '#94a3b8',
                              transform: isOpen ? 'rotate(90deg)' : 'none',
                              transition: 'transform 0.15s',
                              display: 'inline-block',
                            }}
                          >
                            ▶
                          </span>
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: '#1a202c',
                              flex: 1,
                            }}
                          >
                            {topic.name}
                          </span>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              color: '#64748b',
                              background: '#e2e8f0',
                              padding: '2px 8px',
                              borderRadius: 10,
                            }}
                          >
                            {subs.length} sub-topic{subs.length !== 1 ? 's' : ''}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="remove-answer-btn"
                          onClick={e => {
                            e.stopPropagation();
                            setDeleteModal({ open: true, type: 'topic', item: topic });
                          }}
                          title="Delete topic"
                          style={{ marginRight: 12 }}
                        >
                          🗑️
                        </button>
                      </div>

                      {/* Sub-topics */}
                      {isOpen && (
                        <div style={{ borderTop: '1px solid #e2e8f0' }}>
                          {subs.length === 0 ? (
                            <div
                              style={{ color: '#94a3b8', fontSize: '0.8rem', padding: '12px 20px' }}
                            >
                              No sub-topics yet.
                            </div>
                          ) : (
                            subs.map((st, stIdx) => (
                              <SubtopicRow
                                key={st.subTopicId}
                                subtopic={st}
                                showEquationButton={showEquationButton}
                                onDeleted={handleSubtopicDeleted}
                                onMove={handleMoveSubtopic}
                                isFirst={stIdx === 0}
                                isLast={stIdx === subs.length - 1}
                                moving={movingSubtopicIds.has(st.subTopicId)}
                              />
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
